import React from 'react';
import Container from 'components/atomic/atoms/container';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg';

const ICON_SIZES: Record<IconSize, string> = {
  xs: '10px',
  sm: '12px',
  md: '16px',
  lg: '24px',
};

export interface IconWrapperProps {
  /** The icon to wrap */
  children: React.ReactNode;
  /** Size of the icon */
  size?: IconSize;
  /** Color of the icon (applied via fill) */
  color?: string;
  /** Additional class name */
  className?: string;
}

/**
 * IconWrapper - Atomic component for sizing and coloring icons
 * 
 * Wraps any SVG icon and applies consistent sizing and color.
 * 
 * @example
 * ```tsx
 * <IconWrapper size="sm" color="#ffffff">
 *   <SensorDoorIcon />
 * </IconWrapper>
 * ```
 */
export function IconWrapper({
  children,
  size = 'md',
  color,
  className,
}: IconWrapperProps) {
  const iconSize = ICON_SIZES[size];

  return (
    <Container
      component="span"
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: iconSize,
        height: iconSize,
        flexShrink: 0,
        '& svg': {
          width: '100%',
          height: '100%',
          ...(color && { fill: color }),
        },
      }}
    >
      {children}
    </Container>
  );
}

export default IconWrapper;
